import { createInjectionState } from '@vueuse/core';
import { addListener, removeListener } from '@yoda/bridge';
import { ref, watch } from 'vue-demi';

import { isInIOS, isInWebview, isInBrowser } from './device';

type PageVisibilityChangeListener = (visible: boolean) => void;
const pageVisibilityChangeListeners: PageVisibilityChangeListener[] = [];

// TODO: 这个里面的东东都是天翔的，我只是搬运工
// eslint-disable-next-line sonarjs/cognitive-complexity
function addListenerForPageVisibilityChange(listener: PageVisibilityChangeListener) {
    // visibility change
    let isVisible = true;
    // leave/entry state
    let isLeave = false;

    function doCallback(visible: boolean) {
        pageVisibilityChangeListeners.forEach((c) => {
            c(visible);
        });
    }

    function leave() {
        // console.log('[page visible]leave');
        isLeave = true;
        doCallback(false);
    }

    function enter() {
        // console.log('[page visible]enter');
        isLeave = false;
        doCallback(true);
    }

    function foreground() {
        // console.log('[page visible]foreground', isVisible);
        if (isVisible) {
            doCallback(true);
        }
    }

    function background() {
        // console.log('[page visible]background');
        doCallback(false);
    }

    function onVisibilityChange() {
        // console.log('[page visible]visibilityState:' + document.visibilityState);
        // console.log('[page visible]isLeave:' + isLeave);
        const visible = isInBrowser ? document.visibilityState === 'visible' : true;
        if (isLeave) {
            // console.log('[page visible]visibility change after leave');
            return;
        }
        isVisible = visible;
        doCallback(visible);
    }

    // 做一个简单的防止连点
    // 现在有边界情况，快速双击进行路由跳转，但是这个时候还没有跳转完成
    // 会触发这里的 click，导致 visibility 判断失效
    let lastClick = 0;

    function onDocumentClick() {
        if (lastClick + 500 > Date.now()) {
            lastClick = Date.now();
            return;
        }
        lastClick = Date.now();
        // 作为页面可见性的兜底处理
        // 如果用户点击了，说明页面可见，但如果目前页面状态仍为不可见，则需要额外触发一个可见回调
        // 目前发现的case是某个 android 长时间静置回到前台收到前台事件，但是 visibilityState 为 hidden（和混音的处理产生了冲突）
        // https://team.corp.kuaishou.com/task/B309515
        if (!isVisible) {
            // console.log('[page visible]click');
            doCallback(true);
            isVisible = true;
        }
    }

    // 不用重复添加同一个 callback
    if (!pageVisibilityChangeListeners.includes(listener)) {
        pageVisibilityChangeListeners.push(listener);
    }

    // 只用在第一次添加回调时，注册对应的监听事件
    if (pageVisibilityChangeListeners.length === 1 && isInBrowser) {
        document.addEventListener('touchstart', onDocumentClick);

        if (isInWebview()) {
            addListener('native_leave', leave);
            addListener('native_reentry', enter);
            // 兼容性处理，上面的native_reentry 在ios端，webview切换以及app后台都会执行，所以native_foreground只用在android端就可以了。
            if (!isInIOS()) {
                // app显示，锁定屏幕，再打开，重新请求列表数据
                addListener('native_foreground', foreground);
                addListener('native_background', background);
                window.addEventListener('visibilitychange', onVisibilityChange);
            }
        } else {
            window.addEventListener('visibilitychange', onVisibilityChange);
        }
    }

    return function removeEventListener() {
        if (isInBrowser) {
            return;
        }
        // 移除 callback
        const idx = pageVisibilityChangeListeners.indexOf(listener);
        if (idx > -1) {
            pageVisibilityChangeListeners.splice(idx, 1);
        }

        // 如果还存在监听回调，则不用移除所有的监听
        if (pageVisibilityChangeListeners.length > 0) {
            return;
        }

        // 以下需要移除所有监听
        document.removeEventListener('touchstart', onDocumentClick);

        if (isInWebview()) {
            removeListener('native_leave', leave);
            removeListener('native_reentry', enter);
            if (!isInIOS()) {
                removeListener('native_foreground', foreground);
                removeListener('native_background', background);
                window.removeEventListener('visibilitychange', onVisibilityChange);
            }
        } else {
            window.removeEventListener('visibilitychange', onVisibilityChange);
        }
    };
}

export const [provideVisibility, injectVisibility] = createInjectionState(() => {
    const visible = ref(typeof document !== 'undefined' ? document.visibilityState === 'visible' : true);
    addListenerForPageVisibilityChange((val) => {
        if (val !== visible.value) {
            visible.value = val;
        }
    });

    const useVisibilityChange = (listener: PageVisibilityChangeListener) => {
        watch(visible, (val) => {
            listener(val);
        });
    };

    return {
        visible,
        useVisibilityChange,
    };
});
